import React from "react";
import {
  CheckCircle2,
  CircleDashed,
  Eye,
  Trash2,
  RefreshCw,
  AlertCircle,
  LoaderCircle,
  FileText,
  Save,
  Plus,
} from "lucide-react";
import { useOutletContext } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { useTranslation } from "react-i18next";
import "./HomebuyerDocuments.css";
import {
  uploadDossierDocument,
  uploadDossierDocumentNewVersion,
  removeDossierDocumentFile,
  fetchDossierDocumentFileBlob,
} from "./homebuyerDocumentApi";
function getApplicantIdByRole({
  role,
  primaryDossierApplicantId,
  coDossierApplicantId,
}) {
  if (role === "PRIMARY") return primaryDossierApplicantId || null;
  if (role === "CO_APPLICANT") return coDossierApplicantId || null;
  return null;
}
function getRequirementKey(req) {
  const roleKey = req.dossier_applicant_role || "APPLICATION";
  return `${req.document_type_code}__${roleKey}`;
}
function getRequirementFiles(req) {
  return req.files || req.document_files || [];
}
function getLatestVersion(file) {
  return (
    file.latest_version ||
    file.versions?.find((version) => version.is_active) ||
    file.versions?.[0] ||
    null
  );
}
function getUploadedFilesCount(req) {
  return getRequirementFiles(req).filter((file) => file?.is_active !== false)
    .length;
}
function isPdfFile(file) {
  return (
    file?.type === "application/pdf" ||
    file?.type === "dossier/pdf" ||
    file?.name?.toLowerCase().endsWith(".pdf")
  );
}
function getRequirementHint(doc, t) {
  switch (doc.document_type_code) {
    case "last_3_payslips":
      return t("documentHints.last3Payslips");
    case "last_3_bank_statements":
      return t("documentHints.last3BankStatements");
    default:
      if ((doc.max_files ?? 1) > 1) {
        return t("documentHints.multipleFilesAllowed");
      }
      return "";
  }
}

function getDocumentLabel(doc, t) {
  const code = String(doc?.document_type_code || "").trim();

  if (!code) {
    return doc?.document_type_label || "";
  }

  return t(`documentLabels.${code}`, {
    defaultValue: doc?.document_type_label || code,
  });
}

export default function HomebuyerDocuments() {
  const { getAccessTokenSilently } = useAuth0();
  const { t } = useTranslation("homebuyer-documents");
  const {
    dossierId,
    documentRequirements = [],
    registerStepSubmitHandler,
    currentStepKey,
    updateDraftMetaFields,
    openSaveModal,
  } = useOutletContext();
  const requirements = React.useMemo(
    () =>
      [...documentRequirements].sort((a, b) => {
        const aOrder = a.sort_order ?? 999;
        const bOrder = b.sort_order ?? 999;
        return aOrder - bOrder;
      }),
    [documentRequirements],
  );
  const primaryRequirements = React.useMemo(
    () =>
      requirements.filter((req) => req.dossier_applicant_role === "PRIMARY"),
    [requirements],
  );
  const coApplicantRequirements = React.useMemo(
    () =>
      requirements.filter(
        (req) => req.dossier_applicant_role === "CO_APPLICANT",
      ),
    [requirements],
  );
  const dossierRequirements = React.useMemo(
    () => requirements.filter((req) => !req.dossier_applicant_role),
    [requirements],
  );
  const [localRequirements, setLocalRequirements] = React.useState([]);
  const localRequirementsRef = React.useRef([]);
  const [selectedRequirementKey, setSelectedRequirementKey] =
    React.useState(null);
  const [selectedFileId, setSelectedFileId] = React.useState(null);
  const [previewUrl, setPreviewUrl] = React.useState(null);
  const [previewVersionId, setPreviewVersionId] = React.useState(null);
  const [error, setError] = React.useState("");
  const [uploadingRequirementId, setUploadingRequirementId] =
    React.useState(null);
  const [uploadingVersionFileId, setUploadingVersionFileId] =
    React.useState(null);
  const [removingFileId, setRemovingFileId] = React.useState(null);
  const [isLoadingPreview, setIsLoadingPreview] = React.useState(false);
  const [isStartingExtraction, setIsStartingExtraction] = React.useState(false);
  const isContextReady = !!dossierId && requirements.length > 0;
  // Only required documents count toward mandatory dossier completion.
  // Optional documents can be left empty without being marked as completed.
  const requiredDocumentCount = localRequirements.filter(
    (req) => req.is_required === true,
  ).length;
  const completedRequiredCount = localRequirements.filter(
    (req) =>
      req.is_required === true &&
      getUploadedFilesCount(req) >= Math.max(1, req.min_files ?? 1),
  ).length;
  const requiredProgress =
    requiredDocumentCount > 0
      ? (completedRequiredCount / requiredDocumentCount) * 100
      : 100;
  React.useEffect(() => {
    localRequirementsRef.current = requirements;
    setLocalRequirements(requirements);
  }, [requirements]);
  const selectedRequirement = React.useMemo(() => {
    if (!selectedRequirementKey) return null;
    return localRequirements.find(
      (req) => getRequirementKey(req) === selectedRequirementKey,
    );
  }, [localRequirements, selectedRequirementKey]);
  const selectedFile = React.useMemo(() => {
    if (!selectedRequirement || !selectedFileId) return null;
    return getRequirementFiles(selectedRequirement).find(
      (file) =>
        file?.is_active !== false && String(file.id) === String(selectedFileId),
    );
  }, [selectedRequirement, selectedFileId]);
  const selectedVersion = selectedFile ? getLatestVersion(selectedFile) : null;
  React.useEffect(() => {
    if (!selectedRequirementKey && localRequirements.length > 0) {
      const firstReq = localRequirements[0];
      const firstKey = getRequirementKey(firstReq);
      const firstFile = getRequirementFiles(firstReq).find(
        (file) => file?.is_active !== false,
      );
      setSelectedRequirementKey(firstKey);
      setSelectedFileId(firstFile?.id || null);
    }
  }, [localRequirements, selectedRequirementKey]);
  React.useEffect(() => {
    if (!selectedRequirement) return;
    const files = getRequirementFiles(selectedRequirement).filter(
      (file) => file?.is_active !== false,
    );
    if (!files.length) {
      setSelectedFileId(null);
      return;
    }
    const stillExists = files.some(
      (file) => String(file.id) === String(selectedFileId),
    );
    if (!stillExists) {
      setSelectedFileId(files[0].id);
    }
  }, [selectedRequirement, selectedFileId]);
  React.useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);
  React.useEffect(() => {
    async function loadPreview() {
      if (!selectedVersion?.id) {
        if (previewUrl) {
          URL.revokeObjectURL(previewUrl);
        }
        setPreviewUrl(null);
        setPreviewVersionId(null);
        return;
      }
      if (previewVersionId === selectedVersion.id && previewUrl) return;
      try {
        setIsLoadingPreview(true);
        setError("");
        const accessToken = await getAccessTokenSilently();
        const blob = await fetchDossierDocumentFileBlob({
          versionId: selectedVersion.id,
          accessToken,
        });
        const blobUrl = URL.createObjectURL(blob);
        setPreviewUrl((oldUrl) => {
          if (oldUrl) URL.revokeObjectURL(oldUrl);
          return blobUrl;
        });
        setPreviewVersionId(selectedVersion.id);
      } catch (err) {
        setError(err.message || t("preview.loadFailed"));
      } finally {
        setIsLoadingPreview(false);
      }
    }
    loadPreview();
  }, [
    selectedVersion?.id,
    previewVersionId,
    previewUrl,
    getAccessTokenSilently,
    t,
  ]);
  function patchRequirement(requirementId, updater) {
    setLocalRequirements((prev) =>
      prev.map((req) => {
        if (req.id !== requirementId) return req;
        return updater(req);
      }),
    );
  }
  function syncDraftRequirements(nextRequirements) {
    updateDraftMetaFields({
      document_requirements: nextRequirements,
      requires_reextraction: true,
    });
  }
  function commitRequirements(updater) {
    const previousRequirements = localRequirementsRef.current;
    const nextRequirements =
      typeof updater === "function" ? updater(previousRequirements) : updater;
    localRequirementsRef.current = nextRequirements;
    setLocalRequirements(nextRequirements);
    // Update HomebuyerDossierLayout outside the child state updater.
    syncDraftRequirements(nextRequirements);
    return nextRequirements;
  }
  async function handleUploadNewFiles(requirement, filesList) {
    const selectedFiles = Array.from(filesList || []);
    if (!selectedFiles.length) return;
    const invalidFile = selectedFiles.find((file) => !isPdfFile(file));
    if (invalidFile) {
      setError(t("errors.onlyPdf"));
      return;
    }
    const uploadedCount = getUploadedFilesCount(requirement);
    const maxFiles = requirement.max_files ?? 1;
    const remainingSlots = maxFiles - uploadedCount;
    if (selectedFiles.length > remainingSlots) {
      setError(
        t("errors.fileLimitExceeded", {
          count: remainingSlots,
          document: getDocumentLabel(requirement, t),
          defaultValue:
            "You can upload {{count}} more file(s) for {{document}}.",
        }),
      );
      return;
    }
    try {
      setError("");
      setUploadingRequirementId(requirement.id);
      const accessToken = await getAccessTokenSilently();
      const uploadedFiles = [];
      for (const file of selectedFiles) {
        const result = await uploadDossierDocument({
          requirementId: requirement.id,
          file,
          accessToken,
        });
        uploadedFiles.push(
          result.file || {
            id: result.file_id,
            original_filename: result.original_filename,
            mime_type: "application/pdf",
            latest_version: {
              id: result.version_id,
              version_number: result.version_number,
              original_filename: result.original_filename,
              mime_type: "application/pdf",
              is_active: true,
              extraction_status: result.extraction_status,
            },
            versions: [],
          },
        );
      }
      commitRequirements((prev) =>
        prev.map((req) => {
          if (req.id !== requirement.id) return req;
          const existingFiles = getRequirementFiles(req);
          const nextFiles = [...existingFiles, ...uploadedFiles];
          return {
            ...req,
            status: "UPLOADED",
            uploaded_files_count: nextFiles.length,
            files: nextFiles,
          };
        }),
      );
      setSelectedRequirementKey(getRequirementKey(requirement));
      setSelectedFileId(uploadedFiles[0]?.id || null);
    } catch (err) {
      setError(err.message || t("errors.uploadFailed"));
    } finally {
      setUploadingRequirementId(null);
    }
  }
  async function handleUploadNewVersion(requirement, fileItem, file) {
    if (!file) return;
    if (!isPdfFile(file)) {
      setError(t("errors.onlyPdf"));
      return;
    }
    try {
      setError("");
      setUploadingVersionFileId(fileItem.id);
      const accessToken = await getAccessTokenSilently();
      const result = await uploadDossierDocumentNewVersion({
        fileId: fileItem.id,
        file,
        accessToken,
      });
      const nextVersion = result.version || result;
      commitRequirements((prev) =>
        prev.map((req) => {
          if (req.id !== requirement.id) return req;
          const nextFiles = getRequirementFiles(req).map((existingFile) => {
            if (existingFile.id !== fileItem.id) return existingFile;
            return {
              ...existingFile,
              latest_version: nextVersion,
              versions_count: (existingFile.versions_count || 1) + 1,
              versions: [
                nextVersion,
                ...(existingFile.versions || []).map((version) => ({
                  ...version,
                  is_active: false,
                })),
              ],
            };
          });
          return {
            ...req,
            files: nextFiles,
          };
        }),
      );
      setSelectedRequirementKey(getRequirementKey(requirement));
      setSelectedFileId(fileItem.id);
      setPreviewVersionId(null);
    } catch (err) {
      setError(err.message || t("errors.uploadFailed"));
    } finally {
      setUploadingVersionFileId(null);
    }
  }
  async function handleRemoveFile(requirement, fileItem) {
    try {
      setError("");
      setRemovingFileId(fileItem.id);
      const accessToken = await getAccessTokenSilently();
      await removeDossierDocumentFile({
        fileId: fileItem.id,
        accessToken,
      });
      commitRequirements((prev) =>
        prev.map((req) => {
          if (req.id !== requirement.id) return req;
          const nextFiles = getRequirementFiles(req).filter(
            (existingFile) => existingFile.id !== fileItem.id,
          );
          return {
            ...req,
            uploaded_files_count: nextFiles.length,
            status: nextFiles.length > 0 ? "UPLOADED" : "PENDING_UPLOAD",
            files: nextFiles,
          };
        }),
      );
      if (String(selectedFileId) === String(fileItem.id)) {
        setSelectedFileId(null);
      }
    } catch (err) {
      setError(err.message || t("errors.removeFailed"));
    } finally {
      setRemovingFileId(null);
    }
  }
  function getMissingRequiredDocs() {
    return localRequirements.filter((req) => {
      if (!req.is_required) return false;
      const minFiles = req.min_files ?? 1;
      const uploadedCount = getUploadedFilesCount(req);
      return uploadedCount < minFiles;
    });
  }
  React.useEffect(() => {
    async function handleDocumentsStepSubmit() {
      try {
        setError("");
        if (!dossierId) {
          setError(t("errors.dossierMissing"));
          return { ok: false };
        }
        updateDraftMetaFields({
          document_requirements: localRequirements,
          requires_reextraction: true,
        });
        return {
          ok: true,
          updatedDraft: {
            document_requirements: localRequirements,
            requires_reextraction: true,
          },
        };
      } catch (err) {
        setError(
          err.message || t("errors.saveFailed", "Could not save documents."),
        );
        return { ok: false };
      }
    }
    registerStepSubmitHandler(currentStepKey, handleDocumentsStepSubmit);
    return () => {
      registerStepSubmitHandler(currentStepKey, null);
    };
  }, [
    dossierId,
    currentStepKey,
    localRequirements,
    registerStepSubmitHandler,
    t,
    updateDraftMetaFields,
  ]);
  function renderPreview() {
    if (isLoadingPreview) {
      return (
        <div className="hb-doc-preview__empty">
          <LoaderCircle size={22} className="hb-spin" />
          <p>{t("preview.loading")}</p>
        </div>
      );
    }
    if (!previewUrl) {
      return (
        <div className="hb-doc-preview__empty">
          <Eye size={22} />
          <p>{t("preview.empty")}</p>
        </div>
      );
    }
    const mimeType =
      selectedVersion?.mime_type || selectedFile?.mime_type || "";
    const isPdf =
      mimeType === "application/pdf" ||
      mimeType === "dossier/pdf" ||
      selectedVersion?.original_filename?.toLowerCase().endsWith(".pdf");
    if (isPdf) {
      return (
        <iframe
          title={t("preview.iframeTitle")}
          src={previewUrl}
          className="hb-doc-preview__frame"
        />
      );
    }
    return (
      <div className="hb-doc-preview__empty">
        <FileText size={22} />
        <p>{t("preview.unsupported")}</p>
      </div>
    );
  }
  function renderDocumentRows(groupRequirements, groupTitle) {
    if (!groupRequirements.length) return null;
    return (
      <section className="hb-doc-group" aria-label={groupTitle}>
        <div className="hb-doc-group__heading">
          <h2 className="hb-doc-group__title">{groupTitle}</h2>
          <span className="hb-doc-group__count">
            {groupRequirements.length}
          </span>
        </div>
        <div className="hb-doc-group__items">
          {groupRequirements.map((doc) => {
            const key = getRequirementKey(doc);
            const files = getRequirementFiles(doc).filter(
              (file) => file?.is_active !== false,
            );
            const uploadedCount = files.length;
            const isRequired = doc.is_required === true;
            const minFiles = isRequired ? Math.max(1, doc.min_files ?? 1) : 0;
            const maxFiles = Math.max(1, doc.max_files ?? 1);
            const isComplete = isRequired && uploadedCount >= minFiles;
            const hasUploads = uploadedCount > 0;
            const canUploadMore = uploadedCount < maxFiles;
            const isUploading = uploadingRequirementId === doc.id;
            const isSelected = selectedRequirementKey === key;
            const hint = getRequirementHint(doc, t);
            const uploadDisabled =
              !canUploadMore ||
              isUploading ||
              isStartingExtraction ||
              Boolean(removingFileId) ||
              Boolean(uploadingVersionFileId);
            function selectDocument() {
              setSelectedRequirementKey(key);
              setSelectedFileId(files[0]?.id ?? null);
            }
            return (
              <div
                key={doc.id}
                className={[
                  "hb-doc-row",
                  isSelected ? "is-selected" : "",
                  hasUploads ? "is-uploaded" : "",
                  isComplete ? "is-complete" : "",
                  !isRequired ? "is-optional" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                role="button"
                tabIndex={0}
                aria-pressed={isSelected}
                onClick={selectDocument}
                onKeyDown={(event) => {
                  if (event.target !== event.currentTarget) return;
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    selectDocument();
                  }
                }}
              >
                <div className="hb-doc-row__left">
                  <span
                    className={`hb-doc-row__status ${
                      !isRequired
                        ? "hb-doc-row__status--optional"
                        : isComplete
                          ? "hb-doc-row__status--complete"
                          : "hb-doc-row__status--pending"
                    }`}
                    aria-hidden="true"
                  >
                    {!isRequired ? (
                      <FileText size={21} strokeWidth={1.8} />
                    ) : isComplete ? (
                      <CheckCircle2 size={21} strokeWidth={2} />
                    ) : (
                      <CircleDashed size={21} strokeWidth={1.8} />
                    )}
                  </span>
                  <div className="hb-doc-row__text">
                    <div className="hb-doc-row__title">
                      {getDocumentLabel(doc, t)}
                    </div>
                    {hint ? <p className="hb-doc-row__hint">{hint}</p> : null}
                    <div className="hb-doc-row__meta">
                      <span
                        className={`hb-doc-row__badge ${
                          isRequired
                            ? "hb-doc-row__badge--required"
                            : "hb-doc-row__badge--optional"
                        }`}
                      >
                        {isRequired
                          ? t("cards.required", "Required")
                          : t("cards.optional", "Optional")}
                      </span>
                      <span
                        className={`hb-doc-row__badge ${
                          hasUploads ? "hb-doc-row__badge--uploaded" : ""
                        }`}
                      >
                        {isUploading
                          ? t("status.uploading", "Uploading...")
                          : t("cards.uploadedCount", {
                              count: uploadedCount,
                              defaultValue: "{{count}} uploaded",
                            })}
                      </span>
                      {maxFiles > 1 ? (
                        <span className="hb-doc-row__badge hb-doc-row__badge--limit">
                          {t("cards.maxFiles", {
                            count: maxFiles,
                            defaultValue: "Max {{count}} files",
                          })}
                        </span>
                      ) : null}
                    </div>
                  </div>
                </div>
                <label
                  className={`hb-doc-row__action ${
                    uploadDisabled ? "is-disabled" : ""
                  }`}
                  tabIndex={uploadDisabled ? -1 : 0}
                  onClick={(event) => event.stopPropagation()}
                  onKeyDown={(event) => {
                    if (event.key !== "Enter" && event.key !== " ") return;
                    event.preventDefault();
                    event.stopPropagation();
                    if (!uploadDisabled) {
                      event.currentTarget
                        .querySelector('input[type="file"]')
                        ?.click();
                    }
                  }}
                  title={
                    !canUploadMore
                      ? t("cards.maxReached", "File limit reached")
                      : undefined
                  }
                >
                  <input
                    type="file"
                    hidden
                    multiple={maxFiles > 1}
                    accept=".pdf,application/pdf"
                    disabled={uploadDisabled}
                    onChange={(event) => {
                      handleUploadNewFiles(doc, event.target.files);
                      event.target.value = "";
                    }}
                  />
                  {isUploading ? (
                    <LoaderCircle size={16} className="hb-spin" />
                  ) : (
                    <Plus size={16} />
                  )}
                  <span>
                    {maxFiles > 1
                      ? t("buttons.uploadFiles", "Upload file(s)")
                      : t("buttons.upload", "Upload")}
                  </span>
                </label>
              </div>
            );
          })}
        </div>
      </section>
    );
  }
  if (!isContextReady) {
    return (
      <div className="hb-doc-page">
        <h1 className="hb-doc-page__title">{t("title")}</h1>
        <div className="hb-doc-preview__empty hb-doc-preview__empty--full">
          <LoaderCircle size={24} className="hb-spin" />
          <p>{t("loading.requirements")}</p>
        </div>
      </div>
    );
  }
  return (
    <div className="hb-doc-page">
      <div className="hb-step-page__header">
        <h1 className="hb-step-page__title">{t("title")}</h1>
        <button
          type="button"
          className="hb-app-flow__save-btn"
          onClick={openSaveModal}
        >
          <Save size={16} />
          <span>{t("buttons.save")}</span>
        </button>
      </div>
      <div className="hb-doc-help-compact">
        <div className="hb-doc-help-compact__title">{t("help.title")}</div>
        <p className="hb-doc-help-compact__text">{t("help.intro")}</p>
        <ul className="hb-doc-help-compact__list">
          <li>{t("help.steps.scan")}</li>
          <li>{t("help.steps.combine")}</li>
          <li>{t("help.steps.upload")}</li>
        </ul>
        <p className="hb-doc-help-compact__text">
          {t("help.appsPrefix")}{" "}
          <a
            href="https://www.adobe.com/acrobat/mobile/scanner-app.html"
            target="_blank"
            rel="noreferrer"
          >
            Adobe Scan
          </a>
          ,{" "}
          <a
            href="https://www.microsoft.com/en-us/microsoft-365/microsoft-lens"
            target="_blank"
            rel="noreferrer"
          >
            Microsoft Lens
          </a>
          , {t("help.or")}{" "}
          <a
            href="https://www.camscanner.com/"
            target="_blank"
            rel="noreferrer"
          >
            CamScanner
          </a>
          .
        </p>
      </div>
      <div className="hb-doc-page__layout">
        <div className="hb-doc-list">
          <div className="hb-doc-list__overview">
            <div className="hb-doc-list__overview-top">
              <div>
                <span className="hb-doc-list__eyebrow">
                  {t("cards.overviewLabel", "DOCUMENT CHECKLIST")}
                </span>
                <p className="hb-doc-list__intro">{t("intro")}</p>
              </div>
              <span className="hb-doc-list__progress-count">
                {completedRequiredCount}/{requiredDocumentCount}
              </span>
            </div>
            <div
              className="hb-doc-list__progress-track"
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={requiredDocumentCount}
              aria-valuenow={completedRequiredCount}
              aria-label={t(
                "cards.progressLabel",
                "Required documents completed",
              )}
            >
              <span style={{ width: `${requiredProgress}%` }} />
            </div>
            <span className="hb-doc-list__progress-text">
              {t("cards.progressText", {
                completed: completedRequiredCount,
                total: requiredDocumentCount,
                defaultValue:
                  "{{completed}} of {{total}} required documents completed",
              })}
            </span>
          </div>
          {error ? (
            <div className="hb-doc-alert">
              <AlertCircle size={18} />
              <span>{error}</span>
            </div>
          ) : null}
          {renderDocumentRows(
            primaryRequirements.map(
              (req) =>
                localRequirements.find((localReq) => localReq.id === req.id) ||
                req,
            ),
            t("groups.primaryApplicant"),
          )}
          {coApplicantRequirements.length > 0 &&
            renderDocumentRows(
              coApplicantRequirements.map(
                (req) =>
                  localRequirements.find(
                    (localReq) => localReq.id === req.id,
                  ) || req,
              ),
              t("groups.coApplicant"),
            )}
          {dossierRequirements.length > 0 &&
            renderDocumentRows(
              dossierRequirements.map(
                (req) =>
                  localRequirements.find(
                    (localReq) => localReq.id === req.id,
                  ) || req,
              ),
              t("groups.applicationDocuments"),
            )}
        </div>
        <div className="hb-doc-preview">
          {selectedRequirement ? (
            <>
              <div className="hb-doc-preview__header">
                <div>
                  <h2 className="hb-doc-preview__title">
                    {getDocumentLabel(selectedRequirement, t)}
                  </h2>
                  <p className="hb-doc-preview__subtitle">
                    {selectedVersion?.original_filename ||
                      selectedFile?.original_filename ||
                      t("preview.noFile")}
                  </p>
                </div>
                <div className="hb-doc-preview__actions">
                  <label
                    className="hb-doc-preview__btn"
                    tabIndex={
                      selectedFile &&
                      !isStartingExtraction &&
                      !removingFileId &&
                      !uploadingVersionFileId
                        ? 0
                        : -1
                    }
                    onKeyDown={(event) => {
                      if (event.key !== "Enter" && event.key !== " ") return;
                      event.preventDefault();
                      if (
                        selectedFile &&
                        !isStartingExtraction &&
                        !removingFileId &&
                        !uploadingVersionFileId
                      ) {
                        event.currentTarget
                          .querySelector('input[type="file"]')
                          ?.click();
                      }
                    }}
                  >
                    <input
                      type="file"
                      hidden
                      accept=".pdf,application/pdf"
                      disabled={
                        isStartingExtraction ||
                        !selectedFile ||
                        !!removingFileId ||
                        !!uploadingVersionFileId
                      }
                      onChange={(e) =>
                        handleUploadNewVersion(
                          selectedRequirement,
                          selectedFile,
                          e.target.files?.[0],
                        )
                      }
                    />
                    {uploadingVersionFileId === selectedFile?.id ? (
                      <LoaderCircle size={15} className="hb-spin" />
                    ) : (
                      <RefreshCw size={15} />
                    )}
                    <span>{t("buttons.replace")}</span>
                  </label>
                  <button
                    type="button"
                    className="hb-doc-preview__btn hb-doc-preview__btn--danger"
                    onClick={() =>
                      handleRemoveFile(selectedRequirement, selectedFile)
                    }
                    disabled={
                      !selectedFile ||
                      !!uploadingRequirementId ||
                      !!uploadingVersionFileId ||
                      !!removingFileId
                    }
                  >
                    {removingFileId === selectedFile?.id ? (
                      <LoaderCircle size={15} className="hb-spin" />
                    ) : (
                      <Trash2 size={15} />
                    )}
                    <span>{t("buttons.remove")}</span>
                  </button>
                </div>
              </div>
              <div className="hb-doc-file-list">
                {getRequirementFiles(selectedRequirement)
                  .filter((fileItem) => fileItem?.is_active !== false)
                  .map((fileItem) => {
                    const latest = getLatestVersion(fileItem);
                    const isSelected =
                      String(fileItem.id) === String(selectedFileId);
                    return (
                      <button
                        key={fileItem.id}
                        type="button"
                        className={`hb-doc-file-pill ${
                          isSelected ? "is-selected" : ""
                        }`}
                        onClick={() => setSelectedFileId(fileItem.id)}
                      >
                        <FileText size={14} />
                        <span>
                          {latest?.original_filename ||
                            fileItem.original_filename ||
                            `File ${fileItem.id}`}
                        </span>
                      </button>
                    );
                  })}
              </div>
              <div className="hb-doc-preview__body">{renderPreview()}</div>
            </>
          ) : (
            <div className="hb-doc-preview__empty hb-doc-preview__empty--full">
              <Eye size={22} />
              <p>{t("preview.selectPrompt")}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
